import moment from "moment";
import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { getSensorDepths } from "../utils/sensorDepths";

const hasInvalidBmeReading = (reading) =>
  Number(reading?.temperature) === 0 &&
  Number(reading?.humidity) === 0 &&
  Number(reading?.pressure) === 0;

const SensorHistoryChart = ({
  data,
  report,
  visibleLabels = [],
  customTitle,
  sensorDepths,
}) => {
  const [state, setState] = useState(null);
  const { t, i18n } = useTranslation();
  const [legendItems, setLegendItems] = useState([]); // State to hold legend items
  const depths = getSensorDepths(sensorDepths);

  const getChartData = () => {
    let labels = [];
    let dataMv1 = [];
    let dataMv2 = [];
    let dataMv3 = [];
    let temperature = [];
    let humidity = [];
    let tempSubSoil = [];
    let altitude = [];
    let pressure = [];

    data &&
      data.forEach((sensorData) => {
        const dateTime = moment(sensorData.time);
        const hour = dateTime.format("MMM Do HH:00");
        const invalidBme = hasInvalidBmeReading(sensorData);
        dataMv1.push(parseFloat(sensorData.mv1).toFixed(2));
        dataMv2.push(parseFloat(sensorData.mv2).toFixed(2));
        dataMv3.push(parseFloat(sensorData.mv3).toFixed(2));
        temperature.push(invalidBme ? null : parseFloat(sensorData.temperature).toFixed(2));
        altitude.push(parseFloat(sensorData.altitude));
        pressure.push(invalidBme ? null : parseFloat(sensorData.pressure).toFixed(2) / 1000);
        tempSubSoil.push(parseFloat(sensorData.ts).toFixed(2));
        humidity.push(invalidBme ? null : parseFloat(sensorData.humidity).toFixed(2));
        labels.push(hour);
      });

    labels.reverse();
    dataMv1.reverse();
    dataMv2.reverse();
    dataMv3.reverse();
    temperature.reverse();
    altitude.reverse();
    pressure.reverse();
    tempSubSoil.reverse();
    humidity.reverse();

  
    //   labels: labels,
    //   datasets: [
    //     {
    //       label: 'Temperature',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: temperature,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#f46835",
    //       hidden: false, // Initially hidden
    //     },
    //     {
    //       label: 'Humidity',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: humidity,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#2D40E5",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Pressure',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: pressure,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#4BC0C0",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Altitude',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: altitude,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#B7990D",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Temperature subsoil',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: tempSubSoil,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#8621e4",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Niveau 1',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: dataMv1,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#FC5566",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Niveau 2',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: dataMv2,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#3CB371",
    //       hidden: true, // Initially hidden
    //     },
    //     {
    //       label: 'Niveau 3',
    //       lineTension: 0.5,
    //       borderWidth: 2,
    //       data: dataMv3,
    //       fill: false,
    //       backgroundColor: "rgba(75,192,192,0.2)",
    //       borderColor: "#1E90FF",
    //       hidden: true, // Initially hidden
    //     },
    //   ]
    // };
    const fullDatasets = [
      {
        label: "Température de l'air (°C)",
        borderColor: "#f46835",
        data: temperature,
      },
      {
        label: "Humidité de l'air (%)",
        borderColor: "#2D40E5",
        data: humidity,
      },
      {
        label: "Pression de l'air (KPa)",
        borderColor: "#4BC0C0",
        data: pressure,
      },
      {
        label: "Altitude",
        borderColor: "#B7990D",
        data: altitude,
      },
      {
        label: "Temperature subsoil",
        borderColor: "#8621e4",
        data: tempSubSoil,
      },
      {
        label: `Humidité à ${depths.mv1}cm`,
        borderColor: "#FC5566",
        data: dataMv1,
      },
      {
        label: `Humidité à ${depths.mv2}cm`,
        borderColor: "#3CB371",
        data: dataMv2,
      },
      {
        label: `Humidité à ${depths.mv3}cm`,
        borderColor: "#1E90FF",
        data: dataMv3,
      },
    ];
    const filteredDatasets = fullDatasets
      .map((ds) => ({
        ...ds,
        fill: false,
        borderWidth: 2,
        lineTension: 0.5,
        backgroundColor: "rgba(75,192,192,0.2)",
        hidden: report ? false : true,
      }))
      .filter((ds) => {
        return report ? visibleLabels.includes(ds.label) : true;
      });

    setState({
      labels: labels,
      datasets: filteredDatasets,
    });
  };

  useEffect(() => {
    getChartData();
  }, [
    data,
    depths.mv1,
    depths.mv2,
    depths.mv3,
    report,
    JSON.stringify(visibleLabels),
  ]);

  useEffect(() => {
    if (state) {
      // Generate legend items based on dataset labels and fillStyles
      const items = state.datasets.map((dataset, index) => ({
        text: dataset.label,
        fillStyle: dataset.borderColor,
        hidden: true, // Initially hidden
        index,
      }));
      setLegendItems(items);
    }
  }, [state]);

  const handleCheckboxChange = (index) => {
    const updatedDatasets = state.datasets.map((dataset, i) => {
      if (i === index) {
        return { ...dataset, hidden: !dataset.hidden };
      }
      return dataset;
    });

    setState({ ...state, datasets: updatedDatasets });
  };

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ backgroundColor: "white" }}
    >
      {state && (
        <Line
          data={state}
          options={{
            plugins: {
              title: {
                display: true,
                text: customTitle || t("Sensor History"),
                font: {
                  size: 16,
                },
              },

              legend: {
                display: false,
                position: "bottom",
              },
            },

            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: 100,
                    min: 0,
                    stepSize: 25,
                    fontColor: "#000",
                    fontSize: 14,
                  },
                  gridLines: {
                    color: "rgba(0,0,0,0.2)",
                    lineWidth: 1,
                  },
                  scaleOverride: true,
                  scaleSteps: 4,
                  scaleStepWidth: 25,
                  scaleStartValue: 0,
                },
              ],
              xAxes: [
                {
                  ticks: {
                    autoSkip: false,
                    maxRotation: 90,
                    fontColor: "#000",
                    fontSize: 10,
                  },
                  gridLines: {
                    color: "rgba(0,0,0,0.2)",
                    lineWidth: 1,
                  },
                },
              ],
            },
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          padding: "10px",
        }}
      >
    
        {!report &&
          legendItems.map((item, index) => (
            <div
              key={index}
              style={{ display: "inline-block", marginRight: "10px" }}
            >
              <input
                type="checkbox"
                id={`checkbox-${index}`}
                checked={!state.datasets[index].hidden}
                onChange={() => handleCheckboxChange(index)}
              />
              <label
                htmlFor={`checkbox-${index}`}
                style={{ color: item.fillStyle, marginLeft: "4px" }}
              >
                {item.text}
              </label>
            </div>
          ))}
        {report &&
          legendItems.map((item, index) => (
            <div
              key={index}
              style={{ display: "inline-block", marginRight: "10px" }}
            >
              <label
                htmlFor={`checkbox-${index}`}
                style={{ color: item.fillStyle, marginLeft: "4px" }}
              >
                {item.text}
              </label>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SensorHistoryChart;
